import { useMemo, useState } from "react";

import { AlertTriangle, ClipboardCopy, Inbox } from "lucide-react";
import { toast } from "sonner";

import { CollapsibleCard } from "@/components/cinch/CollapsibleCard";
import { EmptyState } from "@/components/cinch/EmptyState";
import { KpiTile } from "@/components/cinch/KpiTile";
import { LinkOut } from "@/components/cinch/LinkOut";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

import type { KbRagSearch, ReviewFilter } from "..";
import {
  useKbReviews,
  useSetReviewState,
  type ArticleReview,
  type ReviewDay,
  type ReviewEvidence,
  type ReviewFinding,
  type ReviewStatus,
} from "../api";
import { ReviewStateControl } from "./ReviewStateControl";

/** "Mon 22 Sep" from "2026-09-22" — the desk is a daily rhythm, so the weekday
 *  is the thing that orients you. */
function dayLabel(date: string): string {
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function matches(a: ArticleReview, filter: ReviewFilter): boolean {
  return filter === "all" || a.status === filter;
}

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Could not copy — select the text and copy it by hand");
  }
}

function Evidence({ items }: { items: ReviewEvidence[] }) {
  if (items.length === 0) return null;
  return (
    <CollapsibleCard
      bare
      title={
        <span className="text-xs font-medium">Why the reviewer thinks so</span>
      }
      meta={`${items.length} source${items.length === 1 ? "" : "s"}`}
      contentClassName="pt-1"
    >
      <ul className="flex flex-col gap-1 text-xs">
        {items.map((e, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-2">
            <Badge variant="outline" className="text-[10px]">
              {e.kind}
            </Badge>
            {e.kind === "code" && e.file_path ? (
              <span className="font-mono">
                {e.repo ? `${e.repo}/` : ""}
                {e.file_path}
                {e.line_start !== null
                  ? `:${e.line_start}${e.line_end !== null ? `-${e.line_end}` : ""}`
                  : ""}
              </span>
            ) : null}
            {e.kind === "tool" && e.name ? (
              <span className="font-mono">{e.name}</span>
            ) : null}
            {e.kind === "article" ? (
              <LinkOut href={e.url} size="xs">
                {e.name ?? e.url ?? "article"}
              </LinkOut>
            ) : null}
            {e.kind === "other" && e.name ? <span>{e.name}</span> : null}
            {e.note ? (
              <span className="text-muted-foreground">{e.note}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </CollapsibleCard>
  );
}

function Finding({ finding }: { finding: ReviewFinding }) {
  if (finding.kind === "fine" && !finding.issue && !finding.suggested_rewrite) {
    return null; // "checked, fine" rows carry no work; the verdict badge says it
  }
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <StatusBadge value={finding.kind} />
        {finding.issue ? (
          <span className="text-sm">{finding.issue}</span>
        ) : null}
      </div>
      {finding.passage ? (
        <blockquote className="mb-2 border-l-2 border-muted-foreground/40 pl-3 text-sm italic text-muted-foreground">
          “{finding.passage}”
        </blockquote>
      ) : null}
      {finding.suggested_rewrite ? (
        <div className="rounded-md bg-muted/50 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {finding.kind === "colour"
                ? "Suggested addition"
                : "Suggested rewrite"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() =>
                copyText(finding.suggested_rewrite ?? "", "Rewrite")
              }
            >
              <ClipboardCopy className="mr-1 h-3 w-3" /> Copy
            </Button>
          </div>
          <MarkdownView
            source={finding.suggested_rewrite}
            className="text-sm"
          />
        </div>
      ) : null}
      <div className="mt-2">
        <Evidence items={finding.evidence} />
      </div>
    </div>
  );
}

function ReviewCard({
  review,
  canAct,
  onOpenArticle,
}: {
  review: ArticleReview;
  canAct: boolean;
  onOpenArticle: (documentId: string) => void;
}) {
  const save = useSetReviewState();
  const actionable = review.findings.filter(
    (f) => f.kind !== "fine" || f.suggested_rewrite,
  );
  return (
    <Card className={cn(review.status === "done" && "opacity-70")}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <LinkOut href={review.url} className="text-base font-semibold">
                {review.title ?? "Untitled article"}
              </LinkOut>
              <StatusBadge value={review.verdict} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {review.category ? <span>{review.category}</span> : null}
              {review.hubspot_updated_at ? (
                <span>
                  · last edited {review.hubspot_updated_at.slice(0, 10)}
                </span>
              ) : null}
              {review.document_id ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => onOpenArticle(review.document_id ?? "")}
                >
                  view indexed article
                </Button>
              ) : null}
            </div>
          </div>
          <StatusBadge value={`review_${review.status}`} />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {review.summary ? <p className="text-sm">{review.summary}</p> : null}
        {actionable.length > 0 ? (
          <div className="flex flex-col gap-2">
            {actionable.map((f) => (
              <Finding key={f.id} finding={f} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing to change — the article checked out.
          </p>
        )}
        <ReviewStateControl
          itemKey={review.review_id}
          state={review}
          canAct={canAct}
          saving={save.isPending}
          compact
          onSave={({ status, note }: { status: ReviewStatus; note: string }) =>
            save.mutate(
              { reviewId: review.review_id, status, note },
              {
                onSuccess: () => toast.success("Review saved"),
                onError: (e) => toast.error(String(e)),
              },
            )
          }
        />
      </CardContent>
    </Card>
  );
}

export function ReviewDeskTab({
  search,
  setSearch,
  canAct,
}: {
  search: KbRagSearch;
  setSearch: (next: Partial<KbRagSearch>) => void;
  canAct: boolean;
}) {
  const { data, isPending, error } = useKbReviews();
  const filter: ReviewFilter = search.rs ?? "new";
  const [showAllDays, setShowAllDays] = useState(false);

  // Default to the newest day that still has something matching the filter,
  // so opening the tab lands on work rather than on an all-done morning.
  const days = useMemo(() => data?.days ?? [], [data]);
  const selectedDay: ReviewDay | undefined = useMemo(() => {
    if (search.day) return days.find((d) => d.date === search.day) ?? days[0];
    return (
      days.find((d) => d.articles.some((a) => matches(a, filter))) ?? days[0]
    );
  }, [days, search.day, filter]);

  if (isPending) return <Skeleton className="h-96" />;

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load the review desk</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  if (!data.available) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>The reviewer&rsquo;s output is unavailable</AlertTitle>
        <AlertDescription>
          The pipeline&rsquo;s status API could not be reached
          {data.reason ? ` (${data.reason})` : ""}. When it is running, five
          articles are reviewed against the current code every weekday at 05:00
          MT and land here.
        </AlertDescription>
      </Alert>
    );
  }

  const visible = selectedDay?.articles.filter((a) => matches(a, filter)) ?? [];
  const pick = (rs: ReviewFilter) =>
    setSearch({ rs: rs === filter ? "all" : rs, day: undefined });
  const dayList = showAllDays ? days : days.slice(0, 10);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="To review"
          value={data.queue.new}
          sub="new suggestions waiting on you"
          tone={data.queue.new ? "warning" : "positive"}
          onClick={() => pick("new")}
          selected={filter === "new"}
        />
        <KpiTile
          label="Accepted"
          value={data.queue.accepted}
          sub="agreed, article not updated yet"
          onClick={() => pick("accepted")}
          selected={filter === "accepted"}
        />
        <KpiTile
          label="Done"
          value={data.queue.done}
          sub="article updated in HubSpot"
          tone="positive"
          onClick={() => pick("done")}
          selected={filter === "done"}
        />
        <KpiTile
          label="Rejected"
          value={data.queue.rejected}
          sub="reviewer was wrong or not worth it"
          onClick={() => pick("rejected")}
          selected={filter === "rejected"}
        />
      </div>

      {days.length === 0 ? (
        <EmptyState icon={<Inbox />} title="No reviews yet">
          The reviewer runs every weekday morning. The first five articles will
          appear here after its first run.
        </EmptyState>
      ) : (
        <div className="grid gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Days
            </p>
            <ul className="flex flex-col gap-1">
              {dayList.map((d) => {
                const open = d.articles.filter(
                  (a) => a.status === "new",
                ).length;
                const active = d.date === selectedDay?.date;
                return (
                  <li key={d.date}>
                    <button
                      type="button"
                      onClick={() => setSearch({ day: d.date })}
                      className={cn(
                        "flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors",
                        active
                          ? "border-brand bg-brand-light text-brand"
                          : "hover:bg-muted",
                      )}
                    >
                      <span className="font-medium">{dayLabel(d.date)}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {open ? `${open} new` : `${d.articles.length} reviewed`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {days.length > 10 ? (
              <Button
                variant="link"
                size="sm"
                className="mt-1 h-auto p-0 text-xs"
                onClick={() => setShowAllDays((v) => !v)}
              >
                {showAllDays ? "Show fewer" : `Show all ${days.length} days`}
              </Button>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 lg:col-span-3">
            {selectedDay ? (
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-base font-semibold">
                  {dayLabel(selectedDay.date)}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {selectedDay.articles.length} article
                    {selectedDay.articles.length === 1 ? "" : "s"} reviewed
                    {selectedDay.judge_model
                      ? ` · ${selectedDay.judge_model}`
                      : ""}
                    {selectedDay.cost_usd !== null
                      ? ` · $${selectedDay.cost_usd.toFixed(2)}`
                      : ""}
                  </span>
                </h3>
                <span className="text-xs text-muted-foreground">
                  Showing {filter === "all" ? "everything" : `“${filter}”`}
                  {filter !== "all" ? (
                    <>
                      {" · "}
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setSearch({ rs: "all" })}
                      >
                        show all
                      </Button>
                    </>
                  ) : null}
                </span>
              </div>
            ) : null}

            {visible.length === 0 ? (
              <EmptyState
                bare
                title={
                  filter === "new"
                    ? "Nothing new for this day"
                    : `Nothing ${filter} for this day`
                }
                className="rounded-lg border border-dashed"
              >
                {filter === "new"
                  ? "Every suggestion from this run has been dealt with. Pick another day, or show all to see what was decided."
                  : "Pick another day or another state above."}
              </EmptyState>
            ) : (
              visible.map((a) => (
                <ReviewCard
                  key={a.review_id}
                  review={a}
                  canAct={canAct}
                  onOpenArticle={(id) =>
                    setSearch({ tab: "articles", article: id })
                  }
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
